import type { CityConfig } from "../city-template/types";

export const owensboroConfig: CityConfig = {
  city: "Owensboro",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "owensboro",
  route: "/cities/owensboro",
  title: "Emergency Passport Owensboro, KY | Fast Passport Renewal Services",
  metaDescription: "Emergency passport and expedited passport services in Owensboro, KY — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Daviess County and Western Kentucky travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Owensboro, KY",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Owensboro and Western Kentucky travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Owensboro%20Kentucky%20downtown%20riverfront%20at%20golden%20hour%20with%20Bluegrass%20Music%20Hall%20of%20Fame%2C%20Ohio%20River%20waterfront%2C%20historic%20district%2C%20warm%20western%20Kentucky%20sunset%20light%2C%20charming%20river%20city%20atmosphere%2C%20clean%20modern%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20vibrant%20Daviess%20County%20scene%20with%20scenic%20bridges%20over%20the%20Ohio%20River&width=1800&height=700&seq=owensboro-hero-001&orientation=landscape",
  heroImageAlt: "Owensboro Kentucky downtown riverfront at golden hour with Bluegrass Music Hall of Fame and Ohio River waterfront",
  geoPosition: "37.7719;-87.1111",
  geoPlacename: "Owensboro, Kentucky",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 3800, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 290 miles from Owensboro via I-65",
  },
  nearbyCitiesZips: [
    { name: "Owensboro", zips: "42301, 42302, 42303" },
    { name: "Henderson", zips: "42420, 42419" },
    { name: "Madisonville", zips: "42431" },
    { name: "Bowling Green", zips: "42101, 42102, 42103, 42104" },
    { name: "Tell City (IN)", zips: "47586" },
    { name: "Evansville (IN)", zips: "47701–47750" },
    { name: "Newburgh (IN)", zips: "47629, 47630" },
    { name: "Boonville (IN)", zips: "47601" },
    { name: "Philpot", zips: "42366" },
    { name: "Whitesville", zips: "42378" },
    { name: "Hartford", zips: "42347" },
    { name: "Livermore", zips: "42352" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Daviess County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Louisville", route: "/cities/louisville" },
    { name: "Fast passport renewal in Henderson", route: "/cities/henderson" },
    { name: "Emergency passport help in Bowling Green", route: "/cities/bowling-green" },
    { name: "Urgent passport services in Evansville", route: "/evansville" },
    { name: "Passport courier services near Madisonville", route: "/madisonville" },
    { name: "Same-day passport help in Paducah", route: "/cities/paducah" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Owensboro remotely via secure courier. We do not have a walk-in office in Owensboro — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Daviess County travelers.",
  fpcOfficeDistance: "Serving all of Daviess County and Western Kentucky",
  serviceAreaIntro: "From downtown Owensboro's riverfront district to the Bluegrass Music Hall of Fame, from the historic bourbon distilleries to the surrounding Western Kentucky farmland — Fast Passport Center serves the entire Owensboro community. We regularly help travelers from Henderson, Madisonville, and Evansville get their passports fast.",
  nearbyCitiesIntro: "We serve all Owensboro neighborhoods and nearby Western Kentucky cities including Henderson, Madisonville, Bowling Green, Tell City (IN), Evansville (IN), Newburgh (IN), Boonville (IN), Philpot, Whitesville, Hartford, and Livermore. If you are in Daviess County, western Kentucky, or the southern Indiana border area, we have you covered.",
  howItWorksIntro: "Most Owensboro travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Owensboro travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Owensboro and Daviess County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Owensboro? Here are the most common ones we hear from Daviess County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Daviess County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Michael B.",
      location: "Owensboro, KY",
      rating: 5,
      text: "Emergency business trip to Japan came up for a bourbon export deal. Fast Passport Center got me a passport in 36 hours. Professional and kept me updated every step.",
      service: "Emergency Passport",
    },
    {
      name: "Patricia D.",
      location: "Henderson, KY",
      rating: 5,
      text: "Family emergency in Ireland. They got me a passport in 24 hours. I was on a plane to Dublin the next day. Cannot recommend them enough.",
      service: "Emergency Passport",
    },
    {
      name: "David R.",
      location: "Owensboro, KY",
      rating: 5,
      text: "Bluegrass music festival tour in Germany. My passport was expired by a month. They got me a renewal in 4 days and I made the tour kickoff in Munich.",
      service: "Passport Renewal",
    },
    {
      name: "Susan W.",
      location: "Madisonville, KY",
      rating: 5,
      text: "First-time passport for our Caribbean cruise. They helped me find the nearest acceptance facility and I had my passport in 5 days. Amazing service for first-timers.",
      service: "New Passport",
    },
  ],
  facilities: [
    {
      id: "owensboro-1",
      name: "Owensboro Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "51 E 4th St",
      city: "Owensboro",
      state: "KY",
      zip: "42303",
      phone: "(270) 684-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Owensboro",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Daviess County. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "owensboro-2",
      name: "Henderson Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 N Main St",
      city: "Henderson",
      state: "KY",
      zip: "42420",
      phone: "(270) 826-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Henderson",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Henderson County and western Daviess County. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "owensboro-3",
      name: "Madisonville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "201 S Main St",
      city: "Madisonville",
      state: "KY",
      zip: "42431",
      phone: "(270) 821-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Madisonville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Hopkins County and eastern Daviess County. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "owensboro-4",
      name: "Bowling Green Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "805 Campbell Ln",
      city: "Bowling Green",
      state: "KY",
      zip: "42104",
      phone: "(270) 843-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Bowling Green",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Warren County and southern Daviess County. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "owensboro-5",
      name: "Evansville Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "800 Sycamore St",
      city: "Evansville",
      state: "IN",
      zip: "47708",
      phone: "(812) 423-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Evansville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for the Evansville metro area. High volume — book appointments 2+ weeks in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "owensboro-6",
      name: "Daviess County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "212 Saint Ann St",
      city: "Owensboro",
      state: "KY",
      zip: "42303",
      phone: "(270) 685-8434",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.2 miles from Daviess County Courthouse",
      website: "https://www.daviessky.org/",
      websiteLabel: "Daviess County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 4.5 hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Daviess County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Daviess County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Owensboro applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Chicago regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Daviess County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Daviess County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Owensboro." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Owensboro address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Owensboro before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Owensboro for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 4.5 hour drive to Chicago." },
    { q: "How fast can I renew my passport in Owensboro?", a: "Fast Passport Center offers fast passport renewal in Owensboro in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. Many cruise lines require your passport to be valid for at least 6 months beyond your travel date. We can expedite your passport renewal in Owensboro in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Owensboro?", a: "Same-day passport appointments are only available at the Chicago Regional Passport Agency — a 4.5 hour drive from Owensboro — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Daviess County residents." },
    { q: "Can I expedite a child passport in Owensboro?", a: "Yes. Fast Passport Center expedites child passports in Owensboro for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Daviess County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Owensboro with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Owensboro?", a: "With Fast Passport Center's emergency courier service, Owensboro travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to drive to the Chicago Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Owensboro?", a: "Popular options include the Owensboro Post Office (42303), Henderson Post Office (42420), Madisonville Post Office (42431), Bowling Green Post Office (42104), Evansville Main Post Office (IN), and Daviess County Clerk (42303). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Owensboro?", a: "Same-day passports are only available at the Chicago Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option — without the 4.5 hour drive to Chicago." },
    { q: "What if I live in a smaller Daviess County town?", a: "We serve the entire Daviess County and surrounding area including Henderson, Madisonville, Bowling Green, Tell City (IN), Evansville (IN), Newburgh (IN), Boonville (IN), Philpot, Whitesville, Hartford, and Livermore. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Owensboro?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
    { q: "Do you help with passport photos in Owensboro?", a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and Walmart locations in Owensboro and Henderson offer passport photo services. We review your photos before submission to ensure they meet State Department standards." },
    { q: "What documents do I need for a first-time passport in Owensboro?", a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk." },
    { q: "Can you help with a child's passport in Owensboro?", a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Daviess County families through the entire process and help schedule acceptance clerk appointments." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Owensboro", address: "2800 Frederica St, Owensboro, KY 42301", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Owensboro", address: "3332 Frederica St, Owensboro, KY 42301", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Owensboro", address: "3151 Burdette Rd, Owensboro, KY 42301", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Owensboro", address: "2000 Triplett St, Owensboro, KY 42303", hours: "Mon–Sun: 8AM–10PM" },
    { name: "CVS Pharmacy — Henderson", address: "2600 US Highway 41 N, Henderson, KY 42420", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Henderson", address: "2800 US Highway 41 N, Henderson, KY 42420", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Madisonville", address: "805 N Main St, Madisonville, KY 42431", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Owensboro", address: "3100 Frederica St, Owensboro, KY 42301", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "Circuit Court Clerk — Daviess County",
      address: "212 Saint Ann St, Owensboro, KY 42303",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Driver's license and REAL ID. Appointment recommended.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Henderson County",
      address: "20 N Main St, Henderson, KY 42420",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Online booking available.",
      featured: false,
    },
    {
      name: "Circuit Court Clerk — Hopkins County",
      address: "56 N Main St, Madisonville, KY 42431",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center for Hopkins County residents.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Warren County",
      address: "429 E 10th Ave, Bowling Green, KY 42101",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Alternative for Warren County and southern Daviess residents. Walk-ins limited.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "OWB",
      name: "Owensboro-Daviess County Regional",
      distance: "4 miles from downtown Owensboro",
      address: "2200 Airport Rd, Owensboro, KY 42301",
      note: "The primary airport for Daviess County. Regional flights with connections to Chicago and Nashville. General aviation and limited commercial service.",
      flightUrl: "https://www.owensboroairport.com/",
      flightLabel: "OWB Airport",
      featured: true,
    },
    {
      code: "EVV",
      name: "Evansville Regional Airport",
      distance: "35 miles from Owensboro",
      address: "7801 Bussing Rd, Evansville, IN 47725",
      note: "Major regional airport for the tri-state area. Accessible via US-231 bridge.",
      flightUrl: "https://www.flyevv.com/",
      flightLabel: "EVV Airport",
      featured: false,
    },
    {
      code: "SDF",
      name: "Louisville Muhammad Ali International",
      distance: "110 miles from Owensboro",
      address: "600 Terminal Dr, Louisville, KY 40209",
      note: "Alternative regional airport for international flights. Accessible via I-65.",
      flightUrl: "https://www.flylouisville.com/",
      flightLabel: "Louisville Airport",
      featured: false,
    },
  ],
};